/**
 * Typed API client for the Ito auth/user-management endpoints.
 *
 * Every call rides the HttpOnly session cookie (`credentials: "include"` —
 * see `lib/session`); the Rust API verifies the session server-side.
 */

import type {
  MeResponse,
  SwitchOrganizationResponse,
} from "./auth";
import type {
  AppSummary,
  AppsPage,
  CommitInfo,
  CreateAppInput,
  GitBlob,
  GitCommits,
  GitCredentialReveal,
  GitInfo,
  GitRefs,
  GitTree,
  UpdateAppInput,
} from "./apps";
import type {
  AvailabilityResponse,
  BoxMessageDetail,
  BoxMessagesPage,
  BoxUsernameClaim,
} from "./box";
import type {
  SearchInstrumentsParams,
  SearchResponse,
  SearchWorkspacesParams,
  WorkspaceSearchResponse,
} from "./search";
import { API_BASE_URL } from "./config";
import { telemetry } from "./telemetry";
import { newSpanId, newTraceId } from "@itoresearch/observability";

/** Error raised when the API returns a non-2xx response. */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Timeout so a hung request can't wedge a query forever. */
const REQUEST_TIMEOUT_MS = 30_000;

function timeoutSignal(): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" &&
    typeof AbortSignal.timeout === "function"
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined;
}

/**
 * Combine the caller's signal (TanStack Query's cancel signal) with the
 * request timeout so either one aborts the fetch.
 */
function combineSignals(
  signal: AbortSignal | undefined,
  timeout: AbortSignal | undefined,
): AbortSignal | undefined {
  if (!signal) return timeout;
  if (!timeout) return signal;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal.aborted || timeout.aborted) {
    controller.abort();
  } else {
    signal.addEventListener("abort", abort, { once: true });
    timeout.addEventListener("abort", abort, { once: true });
  }
  return controller.signal;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /**
   * Optional bearer token. The session normally rides the HttpOnly cookie
   * (`credentials: "include"`); a token is only attached for dev mode, where
   * the API accepts `Bearer dev.<user>.<org>` instead of a cookie.
   */
  authorization?: string;
  /** Caller-provided abort signal, combined with the request timeout. */
  signal?: AbortSignal;
}

/**
 * W3C trace context for the API (spec §43.1.2): every request carries a
 * `traceparent` header derived from the ACTIVE client span (`api:…`), so the
 * server's request span continues the client's trace instead of starting a
 * fresh one. When no span is active, a fresh trace is minted from the shared
 * id helpers. Id/header resolution must never break a request — on failure
 * the header is simply omitted.
 */
function traceparentHeaders(): Record<string, string> {
  try {
    const active = telemetry.currentTraceContext();
    if (active) {
      return { traceparent: `00-${active.traceId}-${active.spanId}-01` };
    }
  } catch {
    /* fall through to a fresh mint */
  }
  try {
    return { traceparent: `00-${newTraceId()}-${newSpanId()}-01` };
  } catch {
    return {};
  }
}

async function request<T>(
  path: string,
  { method = "GET", body, authorization, signal }: RequestOptions,
): Promise<T> {
  // Each API call runs inside a span so failures carry a trace id and are
  // correlated in Axiom with the events logged while the call was in flight.
  return telemetry.withSpan(
    `api:${method} ${path}`,
    async () => {
      const url = `${API_BASE_URL}${path}`;
      const headers: Record<string, string> = { ...traceparentHeaders() };
      if (body !== undefined) headers["Content-Type"] = "application/json";
      if (authorization) headers["Authorization"] = `Bearer ${authorization}`;
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: combineSignals(signal, timeoutSignal()),
      });

      if (!res.ok) {
        let message = `Request failed (${res.status})`;
        try {
          const data = (await res.json()) as { message?: string };
          if (data.message) message = data.message;
        } catch {
          /* non-JSON error body — ignore */
        }
        telemetry.error(message, {
          kind: "api.client",
          method,
          path,
          status: res.status,
        });
        throw new ApiError(res.status, message);
      }

      // 204 (and other empty bodies) carry no JSON to parse.
      const text = await res.text();
      if (!text) return undefined as T;
      return JSON.parse(text) as T;
    },
    { "http.method": method, "http.path": path },
  );
}

/** Authenticated calls to the Ito API (cookie session — see lib/session). */
export const api = {
  me: (signal?: AbortSignal) => request<MeResponse>("/api/v1/me", { signal }),

  /** Re-scope the session's WorkOS token to the org and persist the choice. */
  switchOrganization: (organizationId: string) =>
    request<SwitchOrganizationResponse>("/api/v1/auth/organization", {
      method: "POST",
      body: { organizationId },
    }),

  // Apps — the Ito compute platform.
  apps: {
    list: (
      params?: { search?: string; limit?: number; offset?: number },
      signal?: AbortSignal,
    ) => {
      const query = new URLSearchParams();
      if (params?.search) query.set("search", params.search);
      if (params?.limit !== undefined) query.set("limit", String(params.limit));
      if (params?.offset !== undefined)
        query.set("offset", String(params.offset));
      const qs = query.toString();
      return request<AppsPage>(`/api/v1/apps${qs ? `?${qs}` : ""}`, { signal });
    },
    get: (appId: string, signal?: AbortSignal) =>
      request<AppSummary>(`/api/v1/apps/${appId}`, { signal }),
    create: (input: CreateAppInput) =>
      request<AppSummary>("/api/v1/apps", { method: "POST", body: input }),
    update: (appId: string, input: UpdateAppInput) =>
      request<AppSummary>(`/api/v1/apps/${appId}`, {
        method: "PATCH",
        body: input,
      }),
    remove: (appId: string) =>
      request<void>(`/api/v1/apps/${appId}`, { method: "DELETE" }),

    // Git storage (Harbor) — see crates/api/src/harbor.
    git: {
      info: (appId: string, signal?: AbortSignal) =>
        request<GitInfo>(`/api/v1/apps/${appId}/git`, { signal }),
      provision: (appId: string) =>
        request<GitInfo>(`/api/v1/apps/${appId}/git`, { method: "POST" }),
      /** One-time reveal: the returned apiKey is shown once. */
      createCredential: (appId: string) =>
        request<GitCredentialReveal>(`/api/v1/apps/${appId}/git/credentials`, {
          method: "POST",
        }),
      revokeCredential: (appId: string, credentialId: string) =>
        request<void>(`/api/v1/apps/${appId}/git/credentials/${credentialId}`, {
          method: "DELETE",
        }),

      // Code exploration.
      refs: (appId: string, signal?: AbortSignal) =>
        request<GitRefs>(`/api/v1/apps/${appId}/git/refs`, { signal }),
      tree: (
        appId: string,
        opts?: { ref?: string; path?: string },
        signal?: AbortSignal,
      ) => {
        const query = new URLSearchParams();
        if (opts?.ref) query.set("ref", opts.ref);
        if (opts?.path) query.set("path", opts.path);
        const qs = query.toString();
        return request<GitTree>(
          `/api/v1/apps/${appId}/git/tree${qs ? `?${qs}` : ""}`,
          { signal },
        );
      },
      blob: (
        appId: string,
        oid: string,
        path?: string,
        signal?: AbortSignal,
      ) => {
        const query = new URLSearchParams({ oid });
        if (path) query.set("path", path);
        return request<GitBlob>(
          `/api/v1/apps/${appId}/git/blob?${query.toString()}`,
          { signal },
        );
      },
      commits: (
        appId: string,
        opts?: { ref?: string; limit?: number },
        signal?: AbortSignal,
      ) => {
        const query = new URLSearchParams();
        if (opts?.ref) query.set("ref", opts.ref);
        if (opts?.limit) query.set("limit", String(opts.limit));
        const qs = query.toString();
        return request<GitCommits>(
          `/api/v1/apps/${appId}/git/commits${qs ? `?${qs}` : ""}`,
          { signal },
        );
      },
    },
  },

  // Box — the Ito email platform (relay; see lib/box.ts).
  box: {
    /** The caller's claim; a 404 (unclaimed) surfaces as ApiError(404). */
    myUsername: (signal?: AbortSignal) =>
      request<BoxUsernameClaim>("/api/v1/box/username", { signal }),

    claimUsername: (username: string) =>
      request<BoxUsernameClaim>("/api/v1/box/username", {
        method: "POST",
        body: { username },
      }),

    availability: (username: string) =>
      request<AvailabilityResponse>(
        `/api/v1/box/username/availability?username=${encodeURIComponent(username)}`,
        {},
      ),

    messages: (
      params?: {
        limit?: number;
        offset?: number;
        unreadOnly?: boolean;
      },
      signal?: AbortSignal,
    ) => {
      const query = new URLSearchParams();
      if (params?.limit !== undefined) query.set("limit", String(params.limit));
      if (params?.offset !== undefined)
        query.set("offset", String(params.offset));
      if (params?.unreadOnly) query.set("unreadOnly", "true");
      const qs = query.toString();
      return request<BoxMessagesPage>(
        `/api/v1/box/messages${qs ? `?${qs}` : ""}`,
        { signal },
      );
    },

    message: (messageHash: string, signal?: AbortSignal) =>
      request<BoxMessageDetail>(`/api/v1/box/messages/${messageHash}`, {
        signal,
      }),

    markRead: (messageHash: string) =>
      request<void>(`/api/v1/box/messages/${messageHash}/read`, {
        method: "POST",
      }),
  },

  // Search — Tantivy full-text over the instruments master.
  search: {
    /** Securities-only search (kind pinned to `INSTRUMENT`). */
    instruments: (
      params?: SearchInstrumentsParams,
      authorization?: string,
      signal?: AbortSignal,
    ) => {
      const query = new URLSearchParams();
      if (params?.q) query.set("q", params.q);
      if (params?.limit !== undefined) query.set("limit", String(params.limit));
      if (params?.offset !== undefined)
        query.set("offset", String(params.offset));
      const qs = query.toString();
      return request<SearchResponse>(
        `/api/v1/search/instruments${qs ? `?${qs}` : ""}`,
        { authorization, signal },
      );
    },

    /**
     * Workspace (canvas + structured page) search, org-scoped server-side.
     *
     * Returns the dedicated `WorkspaceSearchResponse` (`workspaceId`/`title`/
     * `kind`/`slug`/`description`) — not the generic master `SearchResponse`.
     */
    workspaces: (
      params?: SearchWorkspacesParams,
      authorization?: string,
    ) => {
      const query = new URLSearchParams();
      if (params?.q) query.set("q", params.q);
      if (params?.limit !== undefined) query.set("limit", String(params.limit));
      if (params?.offset !== undefined)
        query.set("offset", String(params.offset));
      const qs = query.toString();
      return request<WorkspaceSearchResponse>(
        `/api/v1/search/workspaces${qs ? `?${qs}` : ""}`,
        { authorization },
      );
    },
  },
};
